import React from 'react';
import {
  CallMetadata as CallParticipantsMetadata,
  SearchQuery,
} from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2';
import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { TaskSource, TaskStatus, TaskType } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import { Task as TaskRecord } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/task.pb';
import {
  ListTasksRequest,
  Filter as TasksFilter,
} from '@weave/schema-gen-ts/dist/schemas/task-center/v1/task_service.pb';
import {
  AnalysisType as AnalysisTypeEnum,
  AppointmentType as AppointmentTypeEnum,
  CallDirection as CallDirectionEnum,
  Category as CategoryEnum,
  ContactType as ContactTypeEnum,
  SchedulingOutcome as SchedulingOutcomeV2Enum,
  Sentiment as SentimentEnum,
  ServiceQualityFlag as ServiceQualityFlagEnum,
  TimePeriodType as TimePeriodTypeEnum,
  Rating,
  IncludesAnalysis,
  IncludesHydration,
  FilterType_index as FilterTypeEnum,
  CallVisibility as CallVisibilityEnum,
  SortType,
  OfficeHours as OfficeHoursEnum,
  FollowupsSortBy_index as FollowupSortByIndexEnum,
  SortType_index as SortTypeIndexEnum,
  AIOutcome as AIOutcomeEnum,
  SearchType as SearchTypeEnum,
} from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import {
  Call as CallRecord,
  CallSentiment,
} from '@weave/schema-gen-ts/dist/shared/call-intelligence/v2/call_intelligence.pb';
import { AiReceptionistEventType } from '@weave/schema-gen-ts/dist/shared/phone/events/vair/v1/vair.pb';
import { SchemaIO } from '@frontend/schema';
import { SchemaCallIntelV2Service, SchemaTaskService } from '../../api-call-intel/src/service';
import {
  INFINITE_QUERY_ENDPOINT_NAMES,
  MUTATION_ENDPOINT_NAMES,
  QUERY_ENDPOINT_NAMES,
  STANDARD_QUERY_ENDPOINT_NAMES,
  STANDARD_QUERY_TASK_SERVICE_ENDPOINT_NAMES,
  TASK_QUERY_ENDPOINT_NAMES,
} from './constant';

export type StandardQueryEndpointName = (typeof STANDARD_QUERY_ENDPOINT_NAMES)[number];
export type StandardTaskQueryEndpointName = (typeof STANDARD_QUERY_TASK_SERVICE_ENDPOINT_NAMES)[number];
export type InfiniteQueryEndpointName = (typeof INFINITE_QUERY_ENDPOINT_NAMES)[number];
export type QueryEndpointName = (typeof QUERY_ENDPOINT_NAMES)[number];
export type TaskQueryEndpointName = (typeof TASK_QUERY_ENDPOINT_NAMES)[number];

export type MutationEndpointName = (typeof MUTATION_ENDPOINT_NAMES)[number];

export type ServiceStandardQueries = Pick<typeof SchemaCallIntelV2Service, StandardQueryEndpointName>;
export type ServiceTaskStandardQueries = Pick<typeof SchemaTaskService, StandardTaskQueryEndpointName>;
export type ServiceInfiniteQueries = Pick<typeof SchemaCallIntelV2Service, InfiniteQueryEndpointName>;
export type ServiceQueries = Pick<typeof SchemaCallIntelV2Service, QueryEndpointName>;
export type TaskServiceQueries = Pick<typeof SchemaTaskService, TaskQueryEndpointName>;
export type ServiceMutations = Pick<typeof SchemaCallIntelV2Service, MutationEndpointName>;

/**
 *  “IO type” per endpoint for convenience:
 */

export type GetCallsOverviewIO = SchemaIO<(typeof SchemaCallIntelV2Service)['GetCallsOverview']>;
export type GetOverviewStatsIO = SchemaIO<(typeof SchemaCallIntelV2Service)['OverviewStats']>;
export type GetCallEditHistoryIO = SchemaIO<(typeof SchemaCallIntelV2Service)['GetCallEditHistory']>;
export type GetCallMetadataIO = SchemaIO<(typeof SchemaCallIntelV2Service)['GetCallMetadata']>;
export type GetServiceQualityStatsIO = SchemaIO<(typeof SchemaCallIntelV2Service)['GetServiceQualityStats']>;

export type DemoServiceQualityIO = SchemaIO<(typeof SchemaCallIntelV2Service)['DemoServiceQuality']>;

export type SubmitFeedbackIO = SchemaIO<(typeof SchemaCallIntelV2Service)['SubmitFeedback']>;
export type EditAIOutputIO = SchemaIO<(typeof SchemaCallIntelV2Service)['EditAIOutput']>;
export type RecentCallsIO = SchemaIO<(typeof SchemaCallIntelV2Service)['RecentCalls']>;
export type ListCallsIO = SchemaIO<(typeof SchemaCallIntelV2Service)['ListCalls']>;
export type CreateSuggestionIO = SchemaIO<(typeof SchemaCallIntelV2Service)['CreateSuggestion']>;
export type ListFollowupsIO = SchemaIO<(typeof SchemaCallIntelV2Service)['ListFollowups']>;
export type GetCallFollowupsStatsIO = SchemaIO<(typeof SchemaCallIntelV2Service)['GetCallFollowupsStats']>;
export type GetSchedulingAggregateStatsIO = SchemaIO<(typeof SchemaCallIntelV2Service)['GetSchedulingAggregateStats']>;

export type ListTaskIO = SchemaIO<(typeof SchemaTaskService)['ListTasks']>;
export type UpdateTaskIO = SchemaIO<(typeof SchemaTaskService)['UpdateTask']>;
export type DeleteTaskIO = SchemaIO<(typeof SchemaTaskService)['DeleteTask']>;

export enum SchedulingOpportunityEnum {
  SCHEDULING_OPPORTUNITY_IDENTIFIED = 'SCHEDULING_OPPORTUNITY_IDENTIFIED',
  SCHEDULING_OPPORTUNITY_NOT_IDENTIFIED = 'SCHEDULING_OPPORTUNITY_NOT_IDENTIFIED',
}

export enum SchedulingOutcomeEnum {
  SCHEDULING_OUTCOME_UNKNOWN = 'SCHEDULING_OUTCOME_UNKNOWN',
  SCHEDULING_OUTCOME_SCHEDULED = 'SCHEDULING_OUTCOME_SCHEDULED',
  SCHEDULING_OUTCOME_UNSCHEDULED = 'SCHEDULING_OUTCOME_UNSCHEDULED',
}

export type ApiPayloadProps = {
  drillDownOptions?: DrillDownOptions;
  filters: Filters;
  includesAnalysis?: IncludesAnalysis[];
  includesHydration?: IncludesHydration[];
};

export type ApiOverviewStatsParams = {
  filters: Filters;
  includes: StatsIncludes;
};

export type CallIntelTab = 'overview' | 'follow-up' | 'records';

export type CallMetadata = Omit<CallMetadataResponse, 'callMetadata'> & {
  callParticipantsMetadata: CallParticipantsMetadata;
  tasks: Task[];
};

export type CallMetadataResponse = {
  rating?: keyof typeof Rating;
  summary: string;
  transcript: string;
  callMetadata: CallParticipantsMetadata;
};

export interface CallTaskListRequest extends Omit<ListTasksRequest, 'filter'> {
  filter?: Record<number, Partial<TasksFilter>>;
}

export type CallTaskParams = {
  locations?: string[];
  source?: TaskSource;
  callIds?: string[];
  taskStatusFilters?: TaskStatus[];
  taskTypes?: TaskType[];
  demoMetadata?: CallTaskListRequest['demoMetadata'] & {
    callRecordList?: HydratedCallRecord[];
  };
} & Omit<CallTaskListRequest, 'demoMetadata'>;

export type CallInsightAccordion = 'call-tasks' | 'call-summary' | 'call-analysis' | 'call-details' | 'service-quality';

export type ContactTypeFilterEnum = Omit<ContactTypeEnum, ContactTypeEnum.CONTACT_UNKNOWN>;

export type CommonFilterPayloadParams = {
  filters: Filters;
  dateRangeParams?: {
    timePeriod?: TimePeriodTypeEnum;
    timezone?: string;
  };
};

export type DrillDownOptions = {
  index: FilterTypeEnum;
  key: string;
  value: string[];
};

export type DateInterval = {
  startDate: string;
  endDate: string;
};

export type FilterPayload = {
  filter: Partial<Record<FilterTypeEnum, Record<string, unknown>>>;
  limit?: number;
  offset?: number;
  sort_type?: SortTypeIndexEnum;
};

export type Filters = {
  aiOutcomes?: AIOutcomeEnum[];
  aiEvents?: AiReceptionistEventType[];
  appointmentTypes?: AppointmentTypeEnum[];
  callDirection?: CallDirectionEnum;
  contactTypes?: ContactTypeFilterEnum[];
  categories?: CategoryEnum[];
  endDate?: string;
  hideNonAnalyzedCalls?: boolean;
  locations?: string[];
  officeNumbers?: string[];
  officeUsers?: string[];
  schedulingOpportunity?: string;
  schedulingOutcomes?: SchedulingOutcomeEnum[];
  schedulingOutcomeV2s?: SchedulingOutcomeV2Enum[];
  officeHours?: OfficeHoursEnum[];
  sentiments?: SentimentEnum[];
  serviceQualityFlags?: ServiceQualityFlagEnum[];
  startDate?: string;
  taskTypes?: TaskType[];
  sipId?: string[];
};

export type FollowUpItem = {
  allCalls: CallRecord[];
  contact: CallRecord & { contactId: string };
  callTaskMapping: Record<string, Task[]>;
  totalCalls: number;
  totalTasks: number;
};

export type FollowupTaskTypeFilters = {
  startDate?: string;
  endDate?: string;
  locations?: string[];
  officeUsers?: string[];
  contactTypes?: ContactTypeFilterEnum[];
  taskTypes?: TaskType[];
  taskStatus?: TaskStatus[];
  officeHours?: OfficeHoursEnum[];
};

export type GetFollowUpsParams = {
  filters: FollowupTaskTypeFilters;
  sortBy: FollowupSortByIndexEnum;
  sortType?: SortTypeIndexEnum;
  searchQuery?: string;
  pageConfig: PageConfig;
};

export type GetFollowUpsStatsParams = {
  filters: FollowupTaskTypeFilters;
};

export type HighlightedCitation = {
  text: string;
  time: string;
};

export type InfoTipId =
  | 'aiCallOutcome'
  | 'callsAnalyzed'
  | 'callsByAppointmentType'
  | 'callsByCategory'
  | 'callsToReview'
  | 'callEscalationServiceIssue'
  | 'contactType'
  | 'conversionRate'
  | 'excellentService'
  | 'followUpReason'
  | 'followUpSummary'
  | 'ineffectiveTreatmentServiceIssue'
  | 'repeatCallerServiceIssue'
  | 'schedulingOpportunities'
  | 'schedulingOutcome'
  | 'schedulingOutcomeV2'
  | 'sentiments'
  | 'serviceIssues'
  | 'serviceQuality'
  | 'serviceQualityCallVolume'
  | 'serviceQualityPeriodOverPeriod'
  | 'spotLightedCalls'
  | 'tasks'
  | 'taskTypeBilling'
  | 'taskTypeInsurance'
  | 'taskTypeOther'
  | 'taskTypePatientCare'
  | 'taskTypeScheduling'
  | 'taskTypeWaitlist'
  | 'unresolved'
  | 'unscheduledOpportunitiesByLocation'
  | 'activeOpportunities'
  | 'conversionDuringCall';

export type InsightChartType = 'bar' | 'gauge';
export type InsightType = 'scheduled' | 'unresolved';

export type Trend = {
  change: ChangeType;
  percentageChange?: number;
  tooltip?: React.ReactNode;
  partialNote?: string;
  hideWhenNoPrev?: boolean;
  polarity?: Polarity; // Coloring semantics (default 'upGood'); use 'downGood' when lower is better
};

export type KpiItem = {
  id: string;
  title?: React.ReactNode;
  value?: number | null;
  format?: 'integer' | 'percent' | ((n: number | null) => string);
  subtext?: React.ReactNode;
  trend?: Trend;
  noCallsToAnalyze?: boolean;
};

export type ListCallBaseRequestParams = {
  drillDownOptions?: DrillDownOptions;
  filters: Filters;
  showOnlyUnscheduled?: boolean;
  searchQuery?: SearchQuery;
  sortType?: SortType;
  includesAnalysis?: IncludesAnalysis[];
  callVisibility?: CallVisibilityEnum;
};

export type GetCallStatsParams = {
  searchQuery?: SearchQuery;
  filters: Filters;
};

export type ListCallsRequestPayload = ListCallBaseRequestParams & {
  limit: number;
};

export type PageConfig = {
  pageNumber: number;
  pageSize: number;
};

export type PatientProvidedInfo = {
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  country?: string;
  ssn?: string;
  dob?: string;
  faxNumber?: string;
  dependentName?: string[];
  dependentPhone?: string[];
  dependentEmail?: string[];
  dependentDob?: string[];
  dependentSsn?: string[];
  insuranceProvider?: string;
  insurancePhoneNumber?: string;
  insuranceFax?: string;
  insuranceEmail?: string;
  insuranceMemberId?: string[];
};

export type SearchState = { query: string; value: string; searchType: SearchTypeEnum };

export type SelectedTimePeriodKey = string | null;

export type ServiceQualityFlag = Exclude<ServiceQualityFlagEnum, ServiceQualityFlagEnum.FLAG_UNKNOWN>;

export type StatsIncludes = {
  opportunities?: boolean;
  serviceQuality?: boolean;
};

export type ServiceQualityStats = GetServiceQualityStatsIO['output'];

export type Task = {
  citation?: string;
  citationTime?: string;
} & TaskRecord;

export type TaskStats = {
  [key in TaskStatus]?: number;
};

export type TranscriptTimeDataMapping = {
  [time: string]: { popoverLabel: string; hightlightColor: string };
};

export type OfficeUser = {
  firstName?: string;
  lastName?: string;
  status?: string;
  userId?: string;
  userName?: string;
};

export type UnsavedEditChangesConfig = {
  actionType?: 'CLOSE' | 'DISCARD' | 'NO_ACTION' | 'SAVE' | 'SHOW_MODAL';
  dontAskAgain?: boolean;
  entity?: string;
  hasUnsavedChanges: boolean;
};

/**These needs to be organised as per alphabetical order */

export type GetCallEditHistoryResponse = {
  original: CallEditableFields;
  current: CallEditedField[];
};

export type ServiceQualityFlagState = {
  serviceQualityFlag: ServiceQualityFlag;
  isDisabled: boolean;
};

export type CallEditableFields = {
  appointmentTypes?: null | { appointmentType: AppointmentTypeEnum[] };
  callSentiment?: CallSentiment | null;
  categories?: CategoryEnum[];
  contactType?: ContactTypeEnum;
  schedulingOpportunity?: boolean | null;
  schedulingOutcome?: boolean | null;
  schedulingOutcomeV2?: SchedulingOutcomeV2Enum;
  serviceQualityFlags?: ServiceQualityFlagState[];
};

export type CallEditedField = {
  fields: CallEditableFields;
  analysisType: AnalysisTypeEnum;
  editedBy: OfficeUser;
  editedAt: string;
};

export type TransformEditableFieldType = 'edit' | 'restore';

export type ChangeType = 'decreased' | 'increased' | 'noData' | 'unchanged';
export type Polarity = 'upGood' | 'downGood' | 'neutral';

export type ExtendedUpdateTaskRequest = UpdateTaskIO['input'] & {
  callId?: string;
  oldStatus?: TaskStatus;
  taskType?: TaskType;
};

export type ListCallsRequest = SchemaIO<(typeof SchemaCallIntelV2Service)['ListCalls']>['input'];
export type ListCallsResponse = SchemaIO<(typeof SchemaCallIntelV2Service)['ListCalls']>['output'];
export type GetCallsCountResponse = SchemaIO<(typeof SchemaCallIntelV2Service)['GetCallsCount']>['output'];
export type ListFollowupsResponse = SchemaIO<(typeof SchemaCallIntelV2Service)['ListFollowups']>['output'];
export type GetCallFollowupsStatsResponse = SchemaIO<
  (typeof SchemaCallIntelV2Service)['GetCallFollowupsStats']
>['output'];

export type CallIntelSubView = {
  id?: CallIntelSubViewId;
  type?: CallIntelSubViewType;
};

export type CallIntelSubViewType = 'appointment-type' | 'category' | 'sentiment' | 'service-quality';
export type CallIntelSubViewId = string | AppointmentTypeEnum | CategoryEnum | SentimentEnum;

export type { Call as CallRecord } from '@weave/schema-gen-ts/dist/shared/call-intelligence/v2/call_intelligence.pb';

export type {
  DateRange,
  Person,
  GetCallsViewByLocationResponse as CallsViewByLocation,
  CallMetadata as CallParticipantsMetadata,
  SearchQuery,
} from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2/call_intelligence.pb';

export type { IsFieldEdited } from '@weave/schema-gen-ts/dist/shared/call-intelligence/v2/call_intelligence.pb';

export type { PhoneNumber } from '@weave/schema-gen-ts/dist/phonenumbers/phonenumber.pb';

export type CallIntelDemoTriggerSource =
  | 'DASHBOARD_WIDGETS_DEMO'
  | 'FULL_MODULE_DEMO'
  | 'RECENT_CALLS_PAGE'
  | 'CALL_INSIGHT_PANEL';

export type GetOverviewDetailsRequstParams = ApiPayloadProps &
  Pick<GetCallsOverviewIO['input'], 'demoMetadata' | 'isDemo'>;

export type CallIntelSortOption =
  | 'callCountHL'
  | 'callCountLH'
  | 'contactNameAZ'
  | 'contactNameZA'
  | 'lastCallLatest'
  | 'lastCallOldest'
  | 'taskCountHL'
  | 'taskCountLH';
